import React, { useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ReactFlow, {
  Background,
  Controls,
  Position,
  useNodesState,
  useEdgesState,
  Handle,
} from "reactflow";
import "reactflow/dist/style.css";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useGetReviewFlowDataQuery } from "../../useQuery";

import useGetStatusMessageQuery from "../../useQuery/useStatusMessageQuery";

const getNodeColor = (flow, isWaitingStep, isCurrentStep, isDependent, status) => {
  if (isCurrentStep) return "#FFD700";
  if (status === "FAIL") return "#f44336";
  if (isDependent) return "#2550d1";
  if (isWaitingStep) return "#ff5336";
  switch (flow) {
    case "Positive":
      return "#4caf50";
    case "Negative":
      return "#f44336";
    case "Optional":
      return "#ff9800";
    case "Disable":
      return "#9e9e9e";
    default:
      return "#2550d1";
  }
};
const TCCardNode = ({ data, isConnectable }) => {
  const step = data.step;
  return (
    <Box sx={{
      background: "#1a237e",
      color: "#fff",
      padding: "14px",
      borderRadius: "10px",
      border: "2px solid #5c6bc0",
      width: "200px",
      boxShadow: "0px 4px 16px rgba(37,80,209,0.4)",
    }}>
      <Handle type="target" position={Position.Left} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
      <Typography variant="caption" sx={{ fontSize: "9px", color: "#9fa8da", fontWeight: 700, display: "block", mb: 0.5 }}>
        ◈ START OF DEPENDENT TC
      </Typography>
      <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>
        {step.Action}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "10px", color: "#9fa8da", display: "block", mt: 0.5 }}>
        {step.TestData}
      </Typography>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
    </Box>
  );
};
const TCEndNode = ({ data, isConnectable }) => {
  const step = data.step;
  return (
    <Box sx={{
      background: "#1a237e",
      color: "#fff",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "2px dashed rgba(255,255,255,0.4)",
      width: "200px",
      boxShadow: "0px 4px 16px rgba(37,80,209,0.4)",
    }}>
      <Handle type="target" position={Position.Left} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
      <Typography variant="caption" sx={{ fontSize: "9px", color: "#9fa8da", fontWeight: 700, display: "block", mb: 0.5 }}>
        ◈ END OF DEPENDENT TC
      </Typography>
      <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>
        {step.Action}
      </Typography>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
    </Box>
  );
};
const InlineTCStartNode = ({ data, isConnectable }) => {
  const step = data.step;
  return (
    <Box sx={{
      background: "#4a0072",
      color: "#fff",
      padding: "14px",
      borderRadius: "10px",
      border: "2px solid #ce93d8",
      width: "200px",
      boxShadow: "0px 4px 16px rgba(74,0,114,0.4)",
    }}>
      <Handle type="target" position={Position.Left} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
      <Typography variant="caption" sx={{ fontSize: "9px", color: "#ce93d8", fontWeight: 700, display: "block", mb: 0.5 }}>
        ▶ START OF INLINE TC
      </Typography>
      <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>
        {step.Action}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "10px", color: "#ce93d8", display: "block", mt: 0.5 }}>
        {step.TestData}
      </Typography>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
    </Box>
  );
};

const InlineTCEndNode = ({ data, isConnectable }) => {
  const step = data.step;
  return (
    <Box sx={{
      background: "#4a0072",
      color: "#fff",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "2px dashed rgba(206,147,216,0.4)",
      width: "200px",
      boxShadow: "0px 4px 16px rgba(74,0,114,0.4)",
    }}>
      <Handle type="target" position={Position.Left} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
      <Typography variant="caption" sx={{ fontSize: "9px", color: "#ce93d8", fontWeight: 700, display: "block", mb: 0.5 }}>
        ◀ END OF INLINE TC
      </Typography>
      <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>
        {step.Action}
      </Typography>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable}
        style={{ background: "#fff", width: "8px", height: "8px", border: "2px solid #fff" }}
      />
    </Box>
  );
};
const CustomNode = ({ data, isConnectable }) => {
  const step = data.step;
  const isWaitingStep = (step.wait === "Before" || step.wait === "After") && step.waittime > 0;
  const isCurrentStep = data.isCurrentStep;
  const statusMessage = data.statusMessage;
  const nodeColor = getNodeColor(step.Flow, isWaitingStep, isCurrentStep, step._isDependent, step.Status);

  const renderCondition = () => {
    if (!step.condLogic && !step.condexp) return null;

    return (
      <Box
        sx={{
          mt: 0.5,
          p: 0.5,
          background: "rgba(0,0,0,0.15)",
          borderRadius: "4px",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {step.condLogic && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: "10px",
              color: "#4caf50",
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {step.condLogic.toUpperCase()}
          </Typography>
        )}
        {step.condexp && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: "10px",
              color: "#fff",
              fontFamily: "monospace",
              background: "rgba(0,0,0,0.2)",
              p: "2px 4px",
              borderRadius: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {step.condexp}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isCurrentStep && statusMessage && (
        <Box
          sx={{
            position: "absolute",
            top: "-40px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(255, 215, 0, 0.1)",
            border: "1px solid rgba(255, 215, 0, 0.3)",
            borderRadius: "4px",
            padding: "4px 8px",
            minWidth: "200px",
            maxWidth: "300px",
            zIndex: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#FFD700",
              fontSize: "12px",
              fontWeight: 500,
              textAlign: "center",
              display: "block",
            }}
          >
            {statusMessage}
          </Typography>
        </Box>
      )}
      <Tooltip
        title={
          <Box sx={{ p: 1, color: "#fff" }}>
            <Typography variant="subtitle2">Step {step.Step_Number}</Typography>
            <Typography variant="body2">Action: {step.Action}</Typography>
            <Typography variant="body2">Test Data: {step.TestData || "-"}</Typography>
            <Typography variant="body2">Flow: {step.Flow}</Typography>
            {step.Status && (
              <Typography
                variant="body2"
                sx={{ color: step.Status === "FAIL" ? "#f44336" : "#4caf50" }}
              >
                Status: {step.Status}
              </Typography>
            )}
            {typeof step.test_step_duration !== "undefined" && step.test_step_duration !== null && (
              <Typography variant="body2">Duration: {step.test_step_duration}s</Typography>
            )}
            {isWaitingStep && (
              <Typography variant="body2" sx={{ color: "#ff5336" }}>
                Wait Time: {step.waittime}s ({step.wait})
              </Typography>
            )}
            {step.condLogic && <Typography variant="body2">Condition: {step.condLogic}</Typography>}
            {step.condexp && <Typography variant="body2">Expression: {step.condexp}</Typography>}
          </Box>
        }
        arrow
        placement="top"
        enterDelay={200}
        leaveDelay={200}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(0, 0, 0, 0.9)",
              "& .MuiTooltip-arrow": {
                color: "rgba(0, 0, 0, 0.9)",
              },
            },
          },
        }}
      >
        <Box
          sx={{
            background: nodeColor,
            color: "#fff",
            padding: "12px",
            borderRadius: "8px",
            border: isCurrentStep ? "2px solid #FFD700" : "none",
            width: "180px",
            fontSize: "12px",
            fontWeight: isWaitingStep || isCurrentStep ? "bold" : "normal",
            boxShadow: isCurrentStep
              ? "0px 0px 12px rgba(255, 215, 0, 0.5)"
              : "0px 4px 12px rgba(0, 0, 0, 0.2)",
            cursor: "move",
            position: "relative",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: isCurrentStep
                ? "0px 0px 16px rgba(255, 215, 0, 0.6)"
                : "0px 6px 16px rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          <Handle
            type="target"
            position={Position.Left}
            isConnectable={isConnectable}
            style={{
              background: "#fff",
              width: "8px",
              height: "8px",
              border: "2px solid #fff",
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: 600 }}>
                Step {step.Step_Number}
              </Typography>
              {isWaitingStep && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    background: "rgba(0,0,0,0.2)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid rgba(255, 83, 54, 0.3)",
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: "14px", color: "#ff5336" }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#ff5336" }}>
                    {step.waittime}s
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" sx={{ fontSize: "11px", opacity: 0.9, fontWeight: 500 }}>
              {step.Action}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                opacity: 0.8,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                background: "rgba(0,0,0,0.1)",
                padding: "2px 4px",
                borderRadius: "4px",
              }}
            >
              {step.TestData || "-"}
            </Typography>
            {renderCondition()}
          </Box>
          <Handle
            type="source"
            position={Position.Right}
            isConnectable={isConnectable}
            style={{
              background: "#fff",
              width: "8px",
              height: "8px",
              border: "2px solid #fff",
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};


const getMergedSteps = (mainData, reviewFlowData) => {
  const depSteps = {};
  const dependentSteps = reviewFlowData?.dependentTC;

  if (dependentSteps && dependentSteps.length > 0) {
    const depByTCId = new Map();
    dependentSteps.forEach((step) => {
      const tcId = step.Test_Case_Id;
      if (!depByTCId.has(tcId)) depByTCId.set(tcId, []);
      depByTCId.get(tcId).push(step);
    });

    let tcIndex = 0;
    depByTCId.forEach((tcSteps, tcId) => {
      const depEntries = [...tcSteps].sort(
        (a, b) => (Number(a.Step_Number) || 0) - (Number(b.Step_Number) || 0)
      );

      const firstDep = depEntries[0];
      if (firstDep) {
        depSteps[`dep-tc-card-${tcIndex}`] = {
          Step_Number: "TC",
          Action: firstDep.InlineTC || `TC-${tcId}`,
          TestData: `ID: ${tcId}`,
          Flow: "Positive",
          wait: null, waittime: 0,
          condLogic: null, condexp: null,
          _sortOrder: -(depEntries.length + 1) - tcIndex * 1000,
          _isDependent: true,
          _isTCCard: true,
        };
      }

      depEntries.forEach((step, index) => {
        depSteps[`dep-${tcIndex}-${index}`] = {
          ...step,
          _sortOrder: -depEntries.length + index - tcIndex * 1000,
          _isDependent: true,
        };
      });
      depSteps[`dep-tc-end-${tcIndex}`] = {
        Step_Number: "END",
        Action: firstDep.InlineTC || `TC-${tcId}`,
        TestData: "Dependent TC ends here",
        Flow: "Positive",
        wait: null,
        waittime: 0,
        _sortOrder: -tcIndex * 1000 + 1,
        _isDependent: true,
        _isTCEnd: true,
      };
      tcIndex++;
    });
  }

  if (!mainData) return depSteps;
  const inlineTCList = reviewFlowData?.inlineTC;

const inlineTCMap = {};
if (inlineTCList && inlineTCList.length > 0) {
  inlineTCList.forEach((inlineTCEntry) => {
    const tcId = Object.keys(inlineTCEntry)[0];
    const tcContent = inlineTCEntry[tcId];
    const inlineData = tcContent?.data;
    if (inlineData) {
      inlineTCMap[tcId] = Object.entries(inlineData).sort(
        ([, a], [, b]) => (Number(a.Step_Number) || 0) - (Number(b.Step_Number) || 0)
      );
    }
  });
}

const mainResultSteps = reviewFlowData?.data?.data;
const mainResultsByStepId = {};
if (mainResultSteps) {
  Object.values(mainResultSteps).forEach((s) => {
    if (s?.idtest_step != null) mainResultsByStepId[s.idtest_step] = s;
  });
}

const sortedMainEntries = Object.entries(mainData).sort(
  ([, a], [, b]) => (Number(a.Step_Number) || 0) - (Number(b.Step_Number) || 0)
);

const finalSteps = { ...depSteps };
let globalSortOrder = 0;

sortedMainEntries.forEach(([key, rawStep]) => {
  const resultMatch = mainResultsByStepId[rawStep.idtest_step];
  const step = resultMatch
    ? {
        ...rawStep,
        Step_Number: resultMatch.Step_Number,
        Status: resultMatch.Status,
        test_step_duration: resultMatch.test_step_duration,
        screenshot1: resultMatch.screenshot1,
        screenshot1_status: resultMatch.screenshot1_status,
        Failure_Screenshot_Location: resultMatch.Failure_Screenshot_Location,
        Failure_Screenshot_Location_status: resultMatch.Failure_Screenshot_Location_status,
      }
    : rawStep;
  finalSteps[key] = { ...step, _sortOrder: globalSortOrder };
  globalSortOrder++;

  if (step.InlineTC && inlineTCMap[step.InlineTC]) {
    const inlineEntries = inlineTCMap[step.InlineTC];
    const tcId = step.InlineTC;
    const firstInline = inlineEntries[0]?.[1];

    finalSteps[`inline-start-${key}`] = {
      Step_Number: "INLINE",
      Action: firstInline?.InlineTC || `TC-${tcId}`,
      TestData: `Inline TC ID: ${tcId}`,
      Flow: "Positive",
      wait: null, waittime: 0,
      condLogic: null, condexp: null,
      _sortOrder: globalSortOrder,
      _isInline: true,
      _isInlineStart: true,
    };
    globalSortOrder++;

    inlineEntries.forEach(([inlineKey, inlineStep]) => {
      finalSteps[`inline-${key}-${inlineKey}`] = {
        ...inlineStep,
        _sortOrder: globalSortOrder,
        _isInline: true,
      };
      globalSortOrder++;
    });

    finalSteps[`inline-end-${key}`] = {
      Step_Number: "END",
      Action: firstInline?.InlineTC || `TC-${tcId}`,
      TestData: "Inline TC ends here",
      Flow: "Positive",
      wait: null, waittime: 0,
      condLogic: null, condexp: null,
      _sortOrder: globalSortOrder,
      _isInline: true,
      _isInlineEnd: true,
    };
    globalSortOrder++;
  }
});

return finalSteps;
};


const nodeTypes = {
  custom: CustomNode,
  tccard: TCCardNode,
  tcend: TCEndNode,
  inlinestart: InlineTCStartNode,  
  inlineend: InlineTCEndNode,
};

const ExecutionFlowModal = ({ open, onClose, tcid, tcrid, latestRecordings }) => {
  const { data: statusMessage } = useGetStatusMessageQuery(tcrid, {
    enabled:
      !!tcrid &&
      (latestRecordings?.result?.Status === "STARTED" ||
        latestRecordings?.result?.Status === "RUNNING"),
    refetchInterval: 3000,
  });

 const { data: reviewFlowData } = useGetReviewFlowDataQuery(tcid, tcrid, {
  enabled: !!tcid && open,
});

  const getInitialNodes = useCallback(
    (steps) => {
      if (!steps) return [];

      const totalSteps = Object.keys(steps).length;
      const nodesPerRow = Math.min(Math.ceil(Math.sqrt(totalSteps * 1.5)), 8);

      return Object.entries(steps)
  .sort(([, a], [, b]) => (a._sortOrder ?? a.Step_Number) - (b._sortOrder ?? b.Step_Number))
  .map(([key, step], index) => {
        const row = Math.floor(index / nodesPerRow);
        const col = index % nodesPerRow;
        const xOffset = col * 220;
        const yOffset = row * 140;

        const isCurrentStep = statusMessage?.teststepId === step.idtest_step;

        return {
          id: `node-${key}`,
          type: step._isTCCard ? "tccard"
            : step._isTCEnd ? "tcend"
            : step._isInlineStart ? "inlinestart"
            : step._isInlineEnd ? "inlineend"
            : "custom",
          data: {
            step,
            isCurrentStep,
            statusMessage: isCurrentStep ? statusMessage?.executionmessage : null,
          },
          position: {
            x: 30 + xOffset,
            y: 20 + yOffset,
          },
          draggable: true,
        };
      });
    },
    [statusMessage],
  );

  const getInitialEdges = useCallback(
    (steps) => {
      if (!steps) return [];

      const nodesPerRow = Math.min(Math.ceil(Math.sqrt(Object.keys(steps).length * 1.5)), 8);
      const stepKeys = Object.keys(steps).sort(
  (a, b) => (steps[a]._sortOrder ?? steps[a].Step_Number) - (steps[b]._sortOrder ?? steps[b].Step_Number),
);

      return stepKeys.slice(0, -1).map((key, index) => {
        const currentRow = Math.floor(index / nodesPerRow);
        const nextRow = Math.floor((index + 1) / nodesPerRow);
        const isLastInRow = (index + 1) % nodesPerRow === 0;

        let targetKey = stepKeys[index + 1];
        if (isLastInRow && nextRow > currentRow) {
          targetKey = stepKeys[index + 1];
        }

        const sourceStep = steps[key];
        const isWaitingStep = sourceStep.wait === "Before" && sourceStep.waittime > 0;
        const isCurrentStep = statusMessage?.teststepId === sourceStep.idtest_step;
        const edgeColor = getNodeColor(sourceStep.Flow, isWaitingStep, isCurrentStep, sourceStep._isDependent, sourceStep.Status);

        return {
          id: `edge-${key}-${targetKey}`,
          source: `node-${key}`,
          target: `node-${targetKey}`,
          type: "smoothstep",
          animated: isCurrentStep,
          style: {
            stroke: edgeColor,
            strokeWidth: isCurrentStep ? 3 : 2,
            opacity: isCurrentStep ? 1 : 0.8,
          },
          sourceHandle: "right",
          targetHandle: "left",
          markerEnd: {
            type: "arrowclosed",
            color: edgeColor,
            width: 14,
            height: 14,
          },
        };
      });
    },
    [statusMessage],
  );

const [nodes, setNodes, onNodesChange] = useNodesState(
  getInitialNodes(getMergedSteps(latestRecordings?.data, reviewFlowData)),
);
const [edges, setEdges, onEdgesChange] = useEdgesState(
  getInitialEdges(getMergedSteps(latestRecordings?.data, reviewFlowData)),
);

React.useEffect(() => {
  setNodes(getInitialNodes(getMergedSteps(latestRecordings?.data, reviewFlowData)));
  setEdges(getInitialEdges(getMergedSteps(latestRecordings?.data, reviewFlowData)));
}, [latestRecordings, reviewFlowData, statusMessage, getInitialNodes, getInitialEdges, setNodes, setEdges]);

  const onNodeDragStop = useCallback(
    (event, node) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              position: node.position,
            };
          }
          return n;
        }),
      );
    },
    [setNodes],
  );

  const renderContent = () => {
    return (
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop}
        fitView
        fitViewOptions={{
          padding: 0.2,
          minZoom: 0.2,
          maxZoom: 1.5,
        }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        nodeTypes={nodeTypes}
        style={{ background: "#1a1a1a" }}
      >
        <Background color="#2a2a2a" gap={16} size={1} />
        <Controls
          style={{
            background: "#2a2a2a",
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
            border: "1px solid #3a3a3a",
          }}
          showInteractive={false}
        />
      </ReactFlow>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          background: "#1a1a1a",
          color: "#fff",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#2a2a2a",
          borderBottom: "1px solid #3a3a3a",
          color: "#fff",
        }}
      >
        Execution Flow
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, background: "#1a1a1a" }}>
        <Box sx={{ height: "100%", width: "100%" }}>{renderContent()}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionFlowModal;
